import React, { useRef, useState, useEffect } from "react";
import { useSelector } from 'react-redux';
import {
    Affix, Button, Checkbox, CheckPicker, Col, DatePicker, DateRangePicker, Divider, Form, Grid, 
    IconButton, Input, InputGroup, InputPicker, Message, Modal, Panel, Placeholder, Popover, Radio, RadioGroup, RadioTile, RadioTileGroup, Row, Schema, SelectPicker,
    Stack, Steps, Table, TagPicker, Timeline, Toggle, useToaster, Whisper
} from 'rsuite';
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Base64 } from 'js-base64';
import axios from "axios";
import { Icon } from '@rsuite/icons';
import AllProdIcon from '@rsuite/icons/legacy/Archive';
import FileNew from '@rsuite/icons/legacy/FileO';
import ImportIcon from '@rsuite/icons/legacy/Import';
import PageNext from '@rsuite/icons/legacy/PageNext';
import PagePrev from '@rsuite/icons/legacy/PagePrevious';
import DeleteIcon from '@rsuite/icons/legacy/Trash2';
import PDFIcon from '@rsuite/icons/legacy/FilePdfO';
import CopyIcon from '@rsuite/icons/legacy/CopyO';
import ShareIconList from '@rsuite/icons/legacy/Share';
import CheckIcon from '@rsuite/icons/Check';
import CloseIcon from '@rsuite/icons/Close';
import SortIcon from '@rsuite/icons/SortDown';
import FilterIcon from '@rsuite/icons/legacy/Filter';
import PDFCreator from 'components/PDFCreator.js';
import RemoveIcon from '@rsuite/icons/WarningRound';

require('assets/css/newCatalog.css');
require('utils/robotoFonts');

const dateToDMYHM = (date) => {
    let d = date.getDate(), m = date.getMonth() + 1, y = date.getFullYear(), h = date.getHours(), mi = date.getMinutes();
    return '' + (d <= 9 ? '0' + d : d) + '-' + (m <= 9 ? '0' + m : m) + '-' + y + ' ' + (h <= 9 ? '0' + h : h) + ':' + (mi <= 9 ? '0' + mi : mi);
}
const dateToDMY = (date) => {
    let d = date.getDate(), m = date.getMonth() + 1, y = date.getFullYear();
    return '' + (d <= 9 ? '0' + d : d) + '-' + (m <= 9 ? '0' + m : m) + '-' + y;
}
const priceList = ['Sin precio', 'Lista 1', 'Lista 2', 'Lista 3', 'Lista Sucursal'];
const { combine, allowedMaxDays, beforeToday } = DateRangePicker;
const { Column, HeaderCell, Cell } = Table;
const Textarea = React.forwardRef((props, ref) => <Input {...props} as="textarea" ref={ref} maxLength="1024" />);
const ValidateModel = Schema.Model({
    proyectName: Schema.Types.StringType().isRequired('Este campo es obligatorio.'),
    proyectValidity: Schema.Types.ArrayType().isRequired('Este campo es obligatorio.'),
    email: Schema.Types.StringType().isEmail('Please enter a valid email address.')
});
const LocaleDate = {
    sunday: 'Do',
    monday: 'Lu',
    tuesday: 'Ma',
    wednesday: 'Wi',
    thursday: 'Ju',
    friday: 'Vi',
    saturday: 'Sa',
    ok: 'Listo',
    today: 'Hoy',
    yesterday: 'Ayer',
    hours: 'Horas',
    minutes: 'Minutos',
    seconds: 'Segundos',
    last7Days: 'Últimos 7 días'
};
const ItemTypes = {
    COLUMN: "column",
    ROW: "row"
};
const TableRow = ({ children, onDrag, id, rowData, ...rest }) => {
    const ref = React.useRef(null);
    const [{ canDrop, isOver }, drop] = useDrop({
        accept: ItemTypes.ROW,
        collect: (monitor) => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
        drop(item, monitor) { onDrag && onDrag(item.id, rowData.value); }
    });
    const [{ isDragging }, drag] = useDrag({
        item: { id: rowData.value },
        type: ItemTypes.ROW,
        collect: (monitor) => ({ isDragging: monitor.isDragging() })
    });
    const isActive = canDrop && isOver;
    drag(drop(ref));
    const styles = {
        cursor: "grab",
        opacity: isDragging ? 0.5 : 1,
        background: isActive ? "#ddd" : null,
        width: "100%",
        height: "100%",
        borderTop: isActive ? "2px solid #2589f5" : null
    };
    return (
        <div ref={ref} style={styles}>
            {children}
        </div>
    );
}
const ActionCell = ({ rowData, dataKey, callback, ...props }) => {
    return (
        <Cell {...props} className="link-group">
            <IconButton appearance="subtle" icon={<DeleteIcon />} onClick={(e) => callback(rowData)} />
        </Cell>
    );
};

const PopoverFilter = React.forwardRef(({ title, callback, defaultValue, whisper, ...props }, ref) => {
    const inputRef = useRef(null);
    return (
        <Popover ref={ref} title={title} {...props} style={{ width: 300 }} arrow={false} onClick={(e) => e.stopPropagation()}>
            <InputGroup size="md" inside style={{ width: 280 }}>
                <Input placeholder="filtrar" onKeyUp={(e) => { if (e.key === "Enter") whisper.current.close(50); } } onChange={(e) => callback(e)} defaultValue={defaultValue} autoFocus={true} ref={inputRef} />
                <InputGroup.Button onClick={() => { }}>
                    <RemoveIcon onClick={() => { inputRef.current.value = ''; callback(''); whisper.current.close(100); }} />
                </InputGroup.Button>
            </InputGroup>
        </Popover>
    );
});
const FilterHeaderCell = ({ title, popoverTitle, callback, active, ...props }) => {
    const [value, setValue] = useState("");
    const refWhisper = useRef(null);
    return (
        <HeaderCell {...props}>{title}
            <Whisper placement="auto" controlId="control-id-with-filter" trigger="click" ref={refWhisper} onClick={(e) => { e.stopPropagation(); }}
                speaker={<PopoverFilter whisper={refWhisper} title={popoverTitle} callback={(e) => { setValue(e); callback(e); }} defaultValue={value} />}>
                <div className="wise-filter">
                    <IconButton appearance="subtle" size="xs" icon={<FilterIcon color={value.length > 0 ? 'red' : ''} />} />
                </div>
            </Whisper>
        </HeaderCell>
    )
};


function CatalogNew({ setWorking }) {
    const site = useSelector(state => state.site);
    const apiURL = site.site.apiServer; 
    const user = useSelector(state => state.auth.user);
    const shareType = parseInt(site.site.shareType);

    const [globalLoadingData, setGlobalLoadingData] = useState(false);

    const [currentStep, setCurrentStep] = useState(0);
    const [filterType, setFilterType] = useState("filter");
    const dataFetchedFilter = useRef(false);
    const dataFetchedComplete = useRef(false);

    const [selectedProviders, setSelectedProviders] = useState([]);
    const [loadingProviders, setLoadingProviders] = useState(true);
    const [providers, setProviders] = useState([]);
    const allProviders = useRef([]);
    const providerRef = useRef();

    const [selectedRubros, setSelectedRubros] = useState([]);
    const [loadingRubros, setLoadingRubros] = useState(true);
    const [rubros, setRubros] = useState([]);
    const allRubros = useRef([]);
    const rubrosRef = useRef();

    const [selectedSubrubros, setSelectedSubrubros] = useState([]);
    const [loadingSubrubros, setLoadingSubrubros] = useState(true);
    const [subrubros, setSubrubros] = useState([]);
    const allSubrubros = useRef([]);
    const subrubrosRef = useRef();

    const [publications, setPublications] = useState([]);
    const [loadingPublications, setLoadingPublications] = useState(false);
    const [publishSelected, setPublishSelected] = useState(null);

    const [soloOfertas, setSoloOfertas] = useState(false);

    const [selectedArticulos, setSelectedArticulos] = useState([]);
    const [filteredSelectedArticulos, setFilteredSelectedArticulos] = useState([]);
    const [selectedArticulosFilters, setSelectedArticulosFilters] = useState({ value: '', desc: '', prov: '', rubro: '', sub: '' });
    const fnSetSelectedArticulos = (arr) => {
        setSelectedArticulos(arr);
        setFilteredSelectedArticulos(
            arr.filter(x =>
                (selectedArticulosFilters.value.length > 0 ? x.value.toLowerCase().includes(selectedArticulosFilters.value.toLowerCase()) : true) &&
                (selectedArticulosFilters.desc.length > 0 ? x.label.toLowerCase().includes(selectedArticulosFilters.desc.toLowerCase()) : true) &&
                (selectedArticulosFilters.prov.length > 0 ? x.proveedor.toLowerCase().includes(selectedArticulosFilters.prov.toLowerCase()) : true) &&
                (selectedArticulosFilters.rubro.length > 0 ? x.rubro.toLowerCase().includes(selectedArticulosFilters.rubro.toLowerCase()) : true) &&
                (selectedArticulosFilters.sub.length > 0 ? x.subrubro.toLowerCase().includes(selectedArticulosFilters.sub.toLowerCase()) : true))
        );
    }
    const fnFilterSelectedArticulos = (filters) => {
        setSelectedArticulosFilters(filters);
        setFilteredSelectedArticulos(
            selectedArticulos.filter(x =>
                (filters.value.length > 0 ? x.value.toLowerCase().includes(filters.value.toLowerCase()) : true) &&
                (filters.desc.length > 0 ? x.label.toLowerCase().includes(filters.desc.toLowerCase()) : true) &&
                (filters.prov.length > 0 ? x.proveedor.toLowerCase().includes(filters.prov.toLowerCase()) : true) &&
                (filters.rubro.length > 0 ? x.rubro.toLowerCase().includes(filters.rubro.toLowerCase()) : true) &&
                (filters.sub.length > 0 ? x.subrubro.toLowerCase().includes(filters.sub.toLowerCase()) : true))
        );
    }
    const fnClearFilterSelectedArticulos = () => {
        setSelectedArticulosFilters({ value: '', desc: '', prov: '', rubro: '', sub: '' });
        setFilteredSelectedArticulos(selectedArticulos);
    }

    const [currentArticulo, setCurrentArticulo] = useState("");
    const [loadingArticulos, setLoadingArticulos] = useState(true);
    const [loadingArticulosComplete, setLoadingArticulosComplete] = useState(false);
    const [articulos, setArticulos] = useState([]);
    const [sortColumn, setSortColumn] = useState();
    const [sortType, setSortType] = useState();
    const handleInputPicker = useRef();
    
    const [showSelectPublish, setShowSelectPublish] = useState(false);
    const handleShowSelectPublishClose = () => {
        setShowSelectPublish(false);
        setCurrentStep(2);
    }
    const handleShowSelectPublishOpen = () => {
        fetchDataPlublications();
        setShowSelectPublish(true);
    }

    const [showPreview, setShowPreview] = useState(false);

    const [proyectName, setProyectName] = useState("");
    const [proyectValidity, setProyectValidity] = useState(null);
    const [proyectHeaderText, setProyectHeaderText] = useState("Las imágenes son a modo ilustrativo, el producto real puede diferir a la imagen publicada.");
    const [proyectDescription, setProyectDescription] = useState("");
    const [proyectPriceList, setProyectPriceList] = useState(-1);
    const [proyectOfferDate, setProyectOfferDate] = useState(null);
    const [proyectKey, setProyectKey] = useState("");
    const [proyectPrintUser, setProyectPrintUser] = useState(false);
    const [proyectUser, setProyectUser] = useState({});
    const [proyectPDF, setProyectPDF] = useState(false);
    const [proyectGroupType, setProyectGroupType] = useState("0");
    const [validityTo, setValidityTo] = useState('');
    const multiSortRef = useRef();
    const [multiSortValue, setMultiSortValue] = useState([]);

    const [selectData, setSelectData] = useState(['Sin precio', 'Lista 1', 'Lista 2', 'Lista 3', 'Lista Sucursal'].map((item, index) => ({ label: item, value: index })));
    const sorteableColumns = ["value", "label", "proveedor"];
    const sorteableColumnsHR = ["Código", "Descripción", "Proveedor"].map((item, index) => ({
        label: item,
        value: sorteableColumns[index]
    }));

    const fetchDataFilter = async () => await axios.all([
        axios.get(apiURL + "/proveedor", { ref: 'proveedor', headers: { token: JSON.parse(atob(localStorage.getItem("token"))).at }, })
            .catch((e) => { setProviders([]); setLoadingProviders(false); console.log(e); toaster.push(messageError('proveedores'), { placement: "topCenter", duration: 3000 }); }),
        axios.get(apiURL + "/rubro", { ref: 'rubro', headers: { token: JSON.parse(atob(localStorage.getItem("token"))).at }, })
            .catch((e) => { setRubros([]); setLoadingRubros(false); console.log(e); toaster.push(messageError('rubros'), { placement: "topCenter", duration: 3000 }); }),
        axios.get(apiURL + "/subrubro", { ref: 'subrubro', headers: { token: JSON.parse(atob(localStorage.getItem("token"))).at }, })
            .catch((e) => { setSubrubros([]); setLoadingSubrubros(false); console.log(e); toaster.push(messageError('subrubros'), { placement: "topCenter", duration: 3000 }); }),
        axios.get(apiURL + "/articulo?t=filter", { ref: 'articulo', headers: { token: JSON.parse(atob(localStorage.getItem("token"))).at }, })
            .catch((e) => { setArticulos([]); setLoadingArticulos(false); console.log(e); toaster.push(messageError('artículos'), { placement: "topCenter", duration: 3000 }); }),
        axios.get(apiURL + "/listaprecio", { ref: 'listaprecio', headers: { token: JSON.parse(atob(localStorage.getItem("token"))).at }, })
            .catch((e) => { setArticulos([]); setLoadingArticulos(false); console.log(e); toaster.push(messageError('artículos'), { placement: "topCenter", duration: 3000 }); }),
    ]).then((responses) => {
        responses.filter(resp => resp !== undefined).forEach((resp) => {
            if (resp.config.ref === 'proveedor') {
                setProviders(resp.data.data);
                allProviders.current = resp.data.data.map(item => item.value);
                setLoadingProviders(false);
            } else if (resp.config.ref === 'rubro') {
                setRubros(resp.data.data);
                allRubros.current = resp.data.data.map(item => item.value);
                setLoadingRubros(false);
            } else if (resp.config.ref === 'subrubro') {
                setSubrubros(resp.data.data);
                allSubrubros.current = resp.data.data.map(item => item.value);
                setLoadingSubrubros(false);
            } else if (resp.config.ref === 'articulo') {
                setArticulos(resp.data.data.sort((a, b) => {
                    let x = a['label'], y = b['label'];
                    if (typeof x === 'string' && typeof y === 'string')
                        return ('' + x).localeCompare(y);
                    return x - y;
                }));
                setLoadingArticulos(false);
            } else if (resp.config.ref === 'listaprecio') {
                setSelectData(resp.data.data);
            }
        });
    });
    const fetchDataComplete = async () => {
        setLoadingArticulosComplete(true);
        axios.get(apiURL + "/articulo?t=complete", { ref: 'articulo', headers: { token: JSON.parse(atob(localStorage.getItem("token"))).at }, })
            .then((resp) => {
                fnSetSelectedArticulos(resp.data.data);
                setLoadingArticulosComplete(false);
            })
            .catch((e) => {
                fnSetSelectedArticulos([]);
                setLoadingArticulosComplete(false);
                toaster.push(messageError('artículos'), { placement: "topCenter", duration: 3000 });
            })
    }
    const fetchDataPlublications = async () => {
        setLoadingPublications(true);
        setGlobalLoadingData(true);
        await axios
            .get(apiURL + "/catalogo/list", { headers: { token: JSON.parse(atob(localStorage.getItem("token"))).at }, })
            .then((response) => {
                console.log(response);
                setPublications(response.data.data);
            })
            .catch((e) => { console.log(e) })
            .finally(() => { setLoadingPublications(false); setGlobalLoadingData(false); });
    }
    const fetchDataFromPublish = async () => {
        setLoadingArticulosComplete(true);
        axios
            .get(`${apiURL}/articulo/publish/${publishSelected.value}`, { headers: { token: JSON.parse(atob(localStorage.getItem("token"))).at }, })
            .then((resp) => {
                fnSetSelectedArticulos(resp.data.data);
                setLoadingArticulosComplete(false);
            })
            .catch((e) => {
                fnSetSelectedArticulos([]);
                setLoadingArticulosComplete(false);
                toaster.push(messageError('artículos'), { placement: "topCenter", duration: 3000 });
            })
    }
    const fetchDataUsuario = async () => {
        await axios
            .get(apiURL + "/usuario", { headers: { token: JSON.parse(atob(localStorage.getItem("token"))).at }, })
            .then((response) => { setProyectUser({ ...response.data.result[0], printUser: proyectPrintUser }); })
            .catch((e) => { setProyectUser({ contactEmail: '', contactName: '', contactPhone: '', printUser: proyectPrintUser });})
    }
    
    const handleSortColumn = (sc, st) => {
        if (sc && st) {
            fnSetSelectedArticulos([...selectedArticulos].sort((a, b) => {
                let x = a[sc], y = b[sc];
                if (typeof x === 'string' && typeof y === 'string')
                    return st === 'asc' ? ('' + x).localeCompare(y) : ('' + y).localeCompare(x);
                return st === 'asc' ? x - y : y - x;
            }));
        }
        setSortColumn(sc);
        setSortType(st);
    };
    const multiSort = () => {
        console.log(multiSortValue);
        let sortArray = [];
        if (proyectGroupType === '0') {
            sortArray = ['rubro', 'subrubro', ...multiSortValue];
        }
        else if (proyectGroupType === '1') {
            sortArray = ['subrubro', ...multiSortValue];
        }
        fnSetSelectedArticulos([...selectedArticulos].sort(fieldSorter(sortArray)));
    }

    const fieldSorter = (fields) => (a, b) => fields.map(o => {
        let dir = 1;
        if (o[0] === '-') { dir = -1; o = o.substring(1); }
        return a[o] > b[o] ? dir : a[o] < b[o] ? -(dir) : 0;
    }).reduce((p, n) => p ? p : n, 0);

    const toaster = useToaster();
    const message = (<Message showIcon type="info" closable>Artículo existente en la lista</Message>);
    const messageError = (value) => (<Message showIcon type="error" closable>Ocurrió un error al intentar obtener {value}</Message>);
    const messageCopy = (<Message showIcon type="success" closable>Vínculo copiado al portapapeles</Message>);

    const footerStyles = {
        padding: '10px 2px',
        borderTop: '1px solid #e5e5e5'
    };
    const footerButtonStyle = {
        float: 'right',
        marginRight: 10,
        marginTop: 2
    };

    const createCatalogo = async () => {
        if (proyectValidity.length !== 2)
            return;
        let fd = new FormData();
        fd.append('name', Base64.encode(proyectName));
        fd.append('header', Base64.encode(proyectHeaderText));
        fd.append('description', Base64.encode(proyectDescription));
        fd.append('validityFrom', proyectValidity[0].toISOString());
        fd.append('validityTo', proyectValidity[1].toISOString());
        fd.append('priceList', proyectPriceList);
        fd.append('products', Base64.encode(selectedArticulos.map(x => x.value).join('\n')));
        fd.append('offerDate', proyectOfferDate == null ? (new Date(1900, 1, 1)).toISOString() : proyectOfferDate.toISOString());
        fd.append('groupType', proyectGroupType);
        fd.append('showContact', proyectPrintUser)
        await axios
            .postForm(apiURL + '/catalogo', fd, { token: JSON.parse(atob(localStorage.getItem("token"))).at, 'Content-Type': 'multipart/form-data' })
            .then((resp) => {
                if (resp.data.result) {
                    setProyectKey(resp.data.data[0].ID);
                    setValidityTo('El precio se confirmará al concretar la venta - Vigencia: ' +
                        dateToDMY(new Date(proyectValidity[0].toISOString())) + ' al ' +
                        dateToDMY(new Date(proyectValidity[1].toISOString())));
                }
                console.log(resp);
                setCurrentStep(5);
            })
            .catch((error) => console.log(error))
            .finally(() => {
                
            });
    }

    function sort(source, sourceId, targetId) {
        const nextData = source.filter((item) => item.value !== sourceId);
        const dragItem = source.find((item) => item.value === sourceId);
        const index = nextData.findIndex((item) => item.value === targetId);

        nextData.splice(index, 0, dragItem);
        return nextData;
    }
    const handleDragRow = (sourceId, targetId) => {
        setSortColumn("");
        fnSetSelectedArticulos(sort([...selectedArticulos], sourceId, targetId));
    };

    React.useEffect(() => {
        console.log('New Catalog begin');
        fetchDataUsuario();
        console.log(shareType);
        return () => { console.log('New Catalog end'); }
    }, []);

    return (
        <>
            <Panel header={<h4 className="title">Nuevo catálogo</h4>} bordered>
                <Panel>
                    <Steps current={currentStep}>
                        <Steps.Item title="Tipo de selección" stats="wa" />
                        <Steps.Item title={filterType === "all" ? "Completo" : "Filtrar"}
                            status={filterType === "all" || filterType === "load" ? "error" : (currentStep === 1 ? "process" : (currentStep > 1 ? "finish" : "wait"))} />
                        <Steps.Item title={filterType === "all" ? "Completo" : "Seleccionar"}
                            status={filterType === "all" ? "error" : (currentStep === 2 ? "process" : (currentStep > 1 ? "finish" : "wait"))} />
                        <Steps.Item title="Información" />
                        <Steps.Item title="Orden" />
                        <Steps.Item title="Finalizado" />
                    </Steps>
                    <Divider style={{marginBottom: "0px"}} />
                    {
                        currentStep === 0 &&
                        <Panel header={<>
                            <Stack alignItems="baseline" spacing={10}>
                                <Stack.Item grow={1}>
                                    <h5 className="title">Crear nuevo catálogo</h5>
                                    <p style={{ opacity: "0.5" }}>Puede crear un nuevo catálogo seleccionando los artículos o aplicando un filtro. Si necesita el maestro de artículos, puede crear uno con tódos los artículos.</p>
                                </Stack.Item>
                                <IconButton icon={<PageNext />} placement="right" color="green" appearance="primary" onClick={() => {
                                    setSelectedProviders([]);
                                    setSelectedRubros([]);
                                    setSelectedSubrubros([]);
                                    fnSetSelectedArticulos([]);
                                    setCurrentStep(filterType === "all" ? 3 : (filterType === "load" ? (handleShowSelectPublishOpen(), 0) : 1));
                                }}>
                                    Continuar
                                </IconButton>
                            </Stack>
                            </>} className="wizard">
                            <RadioTileGroup defaultValue={filterType} inline aria-label="Tipo de selección" onChange={setFilterType}
                                style={{ maxWidth: "1000px", margin: "30px auto 60px auto" }}>
                                <RadioTile icon={<Icon as={FileNew} />} label="Catálogo en blanco" value="filter">
                                    Crear un catálogo ingreando artículos en base a filtros por proveedor, rubro, subrubro y selección individual.
                                </RadioTile>
                                <RadioTile icon={<Icon as={ImportIcon} />} label="Importar catálogo" value="load">
                                    Crear un catálogo desde un catálogo existente, con la posibilidad de modificar los artículos existentes.
                                </RadioTile>
                                <RadioTile icon={<Icon as={AllProdIcon} />} label="Catálogo completo" value="all">
                                    Se generará el catálogo con todos los artículos que ofrece la empresa.
                                </RadioTile>
                            </RadioTileGroup>
                            
                        </Panel>
                    }
                    {
                        currentStep === 1 && (dataFetchedFilter.current ? true : (fetchDataFilter(), dataFetchedFilter.current = true, true)) &&
                        <Panel header={<>
                            <Stack alignItems="baseline" spacing={10}>
                                <Stack.Item grow={1}>
                                    <h5 className="title">Aplicar filtro</h5>
                                    <p style={{ opacity: "0.5" }}>Aplicando un filtro, en la siguiente pantalla se mostrarán los artículos resultantes.</p>
                                </Stack.Item>
                                <IconButton icon={<PagePrev />} placement="left" appearance="default" onClick={() => setCurrentStep(0)}>
                                    Volver
                                </IconButton>
                                <IconButton icon={<PageNext />} placement="right" color="green" appearance="primary" disabled={!articulos || articulos.length === 0}
                                    onClick={async () => {
                                        setWorking(true);
                                        let anysel = selectedProviders.length > 0 || selectedRubros.length > 0 || selectedSubrubros.length > 0;
                                        let selProviders = selectedProviders;
                                        let selRubros = selectedRubros;
                                        let selSubrubros = selectedSubrubros;
                                        if (anysel) {
                                            if (selectedProviders.length === 0) {
                                                setSelectedProviders(allProviders.current);
                                                selProviders = allProviders.current;
                                            }
                                            if (selectedRubros.length === 0) {
                                                setSelectedRubros(allRubros.current);
                                                selRubros = allRubros.current;
                                            }
                                            if (selectedSubrubros.length === 0) {
                                                setSelectedSubrubros(allSubrubros.current);
                                                selSubrubros = allSubrubros.current;
                                            }
                                        }
                                        let aaa = articulos.filter(a => (
                                            (selProviders.includes(a.pvalue)) &&
                                            (selRubros.includes(a.rvalue)) &&
                                            (selSubrubros.includes(a.svalue)) &&
                                            ((soloOfertas && a.oferta) || !soloOfertas)) ||
                                            selectedArticulos.findIndex(x => x.value === a.value) !== -1
                                        );
                                        setTimeout(() => { setWorking(false); fnClearFilterSelectedArticulos(); fnSetSelectedArticulos(aaa); }, 1000);
                                        setCurrentStep(2);
                                    }}>
                                    Continuar
                                </IconButton>
                            </Stack>
                        </>} className="wizard">
                            <Stack direction="column" justifyContent="center" alignItems="stretch" spacing={20}>
                                <CheckPicker
                                    ref={providerRef} preventOverflow={true} value={selectedProviders}
                                    locale={{ searchPlaceholder: "Buscar proveedor", noResultsText: "No se econtraron resultados" }}
                                    onChange={value => setSelectedProviders(value)} placeholder="Seleccionar" loading={loadingProviders}
                                    label="Proveedores" data={providers} placement="autoVerticalEnd" block sticky renderExtraFooter={() => (
                                        <div style={footerStyles}>
                                            <Checkbox
                                                indeterminate={selectedProviders.length > 0 && selectedProviders.length < allProviders.current.length}
                                                checked={selectedProviders.length === allProviders.current.length}
                                                onChange={(value, checked) => setSelectedProviders(checked ? allProviders.current : [])}
                                            >
                                                Todos
                                            </Checkbox>
                                            <Button
                                                style={footerButtonStyle}
                                                appearance="primary"
                                                size="sm"
                                                onClick={() => providerRef.current.close()}
                                            >
                                                Listo
                                            </Button>
                                        </div>
                                    )} />
                                <CheckPicker
                                    ref={rubrosRef} preventOverflow={true} value={selectedRubros}
                                    locale={{ searchPlaceholder: "Buscar rubro", noResultsText: "No se econtraron resultados" }}
                                    onChange={value => setSelectedRubros(value)} placeholder="Seleccionar" loading={loadingRubros}
                                    label="Rubros" data={rubros} placement="autoVerticalEnd" block sticky renderExtraFooter={() => (
                                        <div style={footerStyles}>
                                            <Checkbox
                                                indeterminate={selectedRubros.length > 0 && selectedRubros.length < allRubros.current.length}
                                                checked={selectedRubros.length === allRubros.current.length}
                                                onChange={(value, checked) => setSelectedRubros(checked ? allRubros.current : [])}
                                            >
                                                Todos
                                            </Checkbox>
                                            <Button
                                                style={footerButtonStyle}
                                                appearance="primary"
                                                size="sm"
                                                onClick={() => rubrosRef.current.close()}
                                            >
                                                Listo
                                            </Button>
                                        </div>
                                    )} />
                                <CheckPicker
                                    ref={subrubrosRef} preventOverflow={true} value={selectedSubrubros}
                                    locale={{ searchPlaceholder: "Buscar proveedor", noResultsText: "No se econtraron resultados" }}
                                    onChange={value => setSelectedSubrubros(value)} placeholder="Seleccionar" loading={loadingSubrubros}
                                    label="Subrubros" data={subrubros} placement="autoVerticalEnd" block sticky renderExtraFooter={() => (
                                        <div style={footerStyles}>
                                            <Checkbox
                                                indeterminate={selectedSubrubros.length > 0 && selectedSubrubros.length < allSubrubros.current.length}
                                                checked={selectedSubrubros.length === allSubrubros.current.length}
                                                onChange={(value, checked) => setSelectedSubrubros(checked ? allSubrubros.current : [])}
                                            >
                                                Todos
                                            </Checkbox>
                                            <Button
                                                style={footerButtonStyle}
                                                appearance="primary"
                                                size="sm"
                                                onClick={() => subrubrosRef.current.close()}
                                            >
                                                Listo
                                            </Button>
                                        </div>
                                    )} />
                                <Message type="info">
                                    <Toggle checkedChildren={<CheckIcon />} unCheckedChildren={<CloseIcon />} checked={soloOfertas} onChange={setSoloOfertas} /> Solo artículos en oferta, con vigencia a partir de <DatePicker disabled={!soloOfertas} placement="auto" oneTap style={{ width: 200 }} value={proyectOfferDate} onChange={(value) => setProyectOfferDate(value) } />
                                </Message>
                            </Stack>
                        </Panel>
                    }
                    {
                        currentStep === 2 && 
                        <Panel header={<>
                            <Stack alignItems="baseline" spacing={10}>
                                <Stack.Item grow={1}>
                                    <h5 className="title">Seleccionar artículos</h5>
                                    <p style={{ opacity: "0.5" }}>Busque e ingrese los productos deseados al listado. Total de productos ingresados al momento <b>{selectedArticulos.length}</b>.</p>
                                </Stack.Item>
                                <Button disabled={selectedArticulos.length === 0} appearance="subtle" startIcon={<DeleteIcon />} onClick={() => fnSetSelectedArticulos([])}>
                                    Limpiar listado
                                </Button>
                                <Affix top={50}>
                                    <IconButton icon={<PagePrev />} placement="left" appearance="default" onClick={() => { setCurrentStep(filterType === "load" ? 0 : 1); }}>
                                        Volver
                                    </IconButton>
                                </Affix>
                                <Affix top={50}>
                                    <IconButton disabled={selectedArticulos.length === 0} icon={<PageNext />} placement="right" color="green" appearance="primary" onClick={() => setCurrentStep(3)}>
                                        Continuar
                                    </IconButton>
                                </Affix>
                            </Stack>
                        </>} className="wizard">
                            <Stack direction="column" justifyContent="center" alignItems="stretch" spacing={20}>
                                <InputPicker ref={handleInputPicker} data={articulos} block loading={loadingArticulos} placeholder="Seleccionar artículo para agregar al listado"
                                    disabledItemValues={articulos.filter(a => (soloOfertas && !a.oferta)).map(a => { return a.value })}
                                    locale={{ emptyMessage: "Sin artículos para seleccionar" }}
                                    searchBy={(keyword, label, item) => keyword == '' ? true : (isNaN(keyword) && item.label.toLowerCase().includes(keyword.toLowerCase()) ? true : (!isNaN(keyword) && item.value.includes(keyword) ? true : false))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { handleInputPicker.current.target.click(); } }}
                                    onChange={value => {
                                        let a = articulos.find(e => e.value === value);
                                        if (a) {
                                            if (!selectedArticulos.some(e => e.value === value)) {
                                                fnSetSelectedArticulos([...selectedArticulos, a]);
                                                setSortColumn("");
                                            } else {
                                                toaster.push(message, { placement: "topCenter", duration: 5000 });
                                            }
                                        }
                                    }} value={currentArticulo} style={{ maxWidth: 500 }}
                                />
                                <Table height={150} autoHeight data={filteredSelectedArticulos} id="table"
                                    sortColumn={sortColumn} locale={{emptyMessage:"Aún sin artículos ingresados"}}
                                    sortType={sortType} 
                                    onSortColumn={handleSortColumn} virtualized>
                                    <Column width={110} sortable>
                                        <FilterHeaderCell title="Cod." popoverTitle="Filtro por código" callback={(e) => {
                                            fnFilterSelectedArticulos({ ...selectedArticulosFilters, value: e });
                                        }} />
                                        <Cell align="center" dataKey="value" />
                                    </Column>
                                    <Column flexGrow={2} sortable>
                                        <FilterHeaderCell title="Descripción" popoverTitle="Filtro por descripción"  callback={(e) => {
                                            fnFilterSelectedArticulos({ ...selectedArticulosFilters, desc: e });
                                        }} />
                                        <Cell dataKey="label" />
                                    </Column>
                                    <Column flexGrow={1} sortable>
                                        <FilterHeaderCell title="Proveedor" active={true} popoverTitle="Filtro por proveedor" callback={(e) => {
                                            fnFilterSelectedArticulos({ ...selectedArticulosFilters, prov: e });
                                        }} />
                                        <Cell dataKey="proveedor" />
                                    </Column>
                                    <Column flexGrow={1} sortable>
                                        <FilterHeaderCell title="Rubro" active={true} popoverTitle="Filtro por rubro" callback={(e) => {
                                            fnFilterSelectedArticulos({ ...selectedArticulosFilters, rubro: e });
                                        }} />
                                        <Cell dataKey="rubro" />
                                    </Column>
                                    <Column flexGrow={1} sortable>
                                        <FilterHeaderCell title="Subrubro" active={true} popoverTitle="Filtro por subrubro" callback={(e) => {
                                            fnFilterSelectedArticulos({ ...selectedArticulosFilters, sub: e });
                                        }} />
                                        <Cell dataKey="subrubro" />
                                    </Column>
                                    {((user.role.includes('user') && user.role.includes('LP1')) || !user.role.includes('user')) && (
                                        <Column width={80} sortable>
                                            <HeaderCell align="right">Lista 1</HeaderCell>
                                            <Cell align="right" dataKey="pventa1">{rowData => `$${" " + Number(rowData.pventa1).toFixed(2)}`}</Cell>
                                        </Column>
                                    )}
                                    {((user.role.includes('user') && user.role.includes('LP2')) || !user.role.includes('user')) && (
                                        <Column width={80} sortable>
                                            <HeaderCell align="right">Lista 2</HeaderCell>
                                            <Cell align="right" dataKey="pventa2">{rowData => `$${" " + Number(rowData.pventa2).toFixed(2)}`}</Cell>
                                        </Column>
                                    )}
                                    {((user.role.includes('user') && user.role.includes('LP3')) || !user.role.includes('user')) && (
                                        <Column width={80} sortable>
                                            <HeaderCell align="right">Lista 3</HeaderCell>
                                            <Cell align="right" dataKey="pventa3">{rowData => `$${" " + Number(rowData.pventa3).toFixed(2)}`}</Cell>
                                        </Column>
                                    )}
                                    {((user.role.includes('user') && user.role.includes('LP4')) || !user.role.includes('user')) && (
                                        <Column width={80} sortable>
                                            <HeaderCell align="right">Lista S</HeaderCell>
                                            <Cell align="right" dataKey="pventaSuc">{rowData => `$${" " + Number(rowData.pventaSuc).toFixed(2)}`}</Cell>
                                        </Column>
                                    )}
                                    <Column width={46}>
                                        <HeaderCell>
                                            <DeleteIcon />
                                        </HeaderCell>
                                        <ActionCell dataKey="value" callback={(rowData) => fnSetSelectedArticulos(selectedArticulos.filter(item => item.value !== rowData.value)) } />
                                    </Column>
                                </Table>
                            </Stack>

                        </Panel>
                    }
                    {
                        currentStep === 3 &&
                        <Panel header={<>
                            <Stack alignItems="baseline" spacing={10}>
                                <Stack.Item grow={1}>
                                    <h5 className="title">Información sobre el catálogo</h5>
                                    <p style={{ opacity: "0.5" }}>Por favor, complete al menos el nombre del catálogo, vigencia y lista de precios para continuar.</p>
                                </Stack.Item>
                                <Affix top={50}>
                                    <IconButton icon={<PagePrev />} placement="left" appearance="default" onClick={() => { fnClearFilterSelectedArticulos(); setCurrentStep(filterType === "all" ? 0 : 2) }}>
                                        Volver
                                    </IconButton>
                                </Affix>
                                <Affix top={50}>
                                    <IconButton disabled={proyectName.length === 0 ||
                                        (proyectValidity != null ? proyectValidity.length !== 2 : true ) ||
                                        proyectPriceList === -1} icon={<PageNext />} placement="right" color="green" appearance="primary" onClick={() => setCurrentStep(4)}>
                                        Continuar
                                    </IconButton>
                                </Affix>
                            </Stack>
                        </>} className="wizard">
                            
                            <Form layout="horizontal" model={ValidateModel} checkTrigger="change" className="basic-form">
                                <Form.Group controlId="proyectName">
                                    <Form.ControlLabel>Nombre del catálogo</Form.ControlLabel>
                                    <Form.Control name="proyectName" maxLength="50" value={proyectName} onChange={(value) => setProyectName(value) } />
                                </Form.Group>
                                {user.role.includes('adminx') && (
                                    <Form.Group controlId="proyectHeaderText">
                                        <Form.ControlLabel>Descargo legal</Form.ControlLabel>
                                        <Form.Control rows={2} name="proyectHeaderText" accepter={Textarea} maxLength="255" value={proyectHeaderText} onChange={(value) => setProyectHeaderText(value)} />
                                    </Form.Group>
                                )}
                                <Form.Group controlId="proyectDescription">
                                    <Form.ControlLabel>Descripción</Form.ControlLabel>
                                    <Form.Control rows={1} name="proyectDescription" accepter={Textarea} maxLength="1024" value={proyectDescription} onChange={(value) => setProyectDescription(value)} />
                                </Form.Group>
                                <Form.Group controlId="proyectValidity">
                                    <Form.ControlLabel>Vigencia</Form.ControlLabel>
                                    <Form.Control name="proyectValidity" accepter={DateRangePicker}
                                        format="dd-MM-yyyy HH:mm" placement="auto" locale={LocaleDate}
                                        shouldDisableDate={combine(allowedMaxDays(90), beforeToday())} preventOverflow={true} ranges={[]}
                                        onChange={(value) => {
                                            setProyectValidity(value);
                                            setValidityTo('El precio se confirmará al concretar la venta - Vigencia: ' +
                                                dateToDMY(new Date(value[0].toISOString())) + ' al ' +
                                                dateToDMY(new Date(value[1].toISOString())));
                                        }} value={proyectValidity}
                                        defaultCalendarValue={[new Date(new Date().setHours(0, 0, 0, 0)), new Date(new Date().setHours(23, 59, 59, 0))]} />
                                </Form.Group>
                                <Form.Group controlId="proyectPriceList">
                                    <Form.ControlLabel>Lista de precio</Form.ControlLabel>
                                    <Form.Control name="proyectPriceList" accepter={SelectPicker} data={
                                        selectData.filter((x) => (x.value === 0 && user.role.includes('LP0')) ||
                                            (x.value === 1 && user.role.includes('LP1')) ||
                                            (x.value === 2 && user.role.includes('LP2')) ||
                                            (x.value === 3 && user.role.includes('LP3')) ||
                                            (x.value === 4 && user.role.includes('LP4')) ||
                                            !user.role.includes('user'))
                                    } style={{ width: 328 }} searchable={false}
                                        value={proyectPriceList} onChange={(value) => setProyectPriceList(value)} placement="autoVerticalStart"/>
                                </Form.Group>
                                <Form.Group>
                                    <Form.ControlLabel>Mostrar datos de contacto</Form.ControlLabel>
                                    <div style={{paddingTop:5}}>
                                        <Toggle checkedChildren={<CheckIcon />} unCheckedChildren={<CloseIcon />} checked={proyectPrintUser} onChange={setProyectPrintUser} />
                                    </div>
                                </Form.Group>
                            </Form>
                            
                        </Panel>
                    }
                    {
                        currentStep === 4 && (filterType !== "all" || (dataFetchedComplete.current ? true : (fetchDataComplete(), dataFetchedComplete.current = true, true))) &&
                        <Panel header={<>
                            <Stack alignItems="baseline" spacing={10}>
                                <Stack.Item grow={1}>
                                    <h5 className="title">Ordenamiento y previsualización</h5>
                                    <p style={{ opacity: "0.5" }}>En esta tabla puede establecer el órden de aparción de los {selectedArticulos.length} productos seleccionados.</p>
                                </Stack.Item>
                                <Button appearance="subtle" startIcon={<PDFIcon />} onClick={() => setShowPreview(true)}>
                                    Previsualizar catálogo
                                </Button>
                                <Affix top={50}>
                                    <IconButton icon={<PagePrev />} placement="left" appearance="default" onClick={() => setCurrentStep(3)}>
                                        Volver
                                    </IconButton>
                                </Affix>
                                <Affix top={50}>
                                    <IconButton icon={<PageNext />} placement="right" color="green" appearance="primary" onClick={() => createCatalogo()}>
                                        Finalizar
                                    </IconButton>
                                </Affix>
                            </Stack>
                        </>}>
                            <Stack alignItems="center" spacing={10}>
                                <p> Agrupar los artículos por </p>
                                <RadioGroup name="radioList" inline onChange={(value, event) => setProyectGroupType(value)} defaultValue={proyectGroupType}>
                                    <Radio value="0">Rubro</Radio>
                                    <Radio value="1">Subrubro</Radio>
                                </RadioGroup>
                            </Stack>
                            <Stack alignItems="center" spacing={10}>
                                <Stack.Item grow={1}>
                                    <TagPicker ref={multiSortRef} data={sorteableColumnsHR} style={{ marginTop: 10, marginBottom: 10 }} block searchable={false}
                                        disabled={loadingArticulosComplete}
                                        onChange={(value) => setMultiSortValue(value)} locale={{ placeholder: "especifique ordenamiento" }} renderExtraFooter={() => (
                                            <div style={footerStyles}>
                                                <Button
                                                    appearance="primary"
                                                    size="sm"
                                                    onClick={() => { multiSortRef.current.close(); }}
                                                >
                                                    Listo
                                                </Button>
                                            </div>
                                        )} />
                                </Stack.Item>
                                <Button disabled={multiSortValue.length === 0} appearance="primary" startIcon={<SortIcon />} onClick={() => multiSort()}>
                                    Aplicar orden
                                </Button>
                            </Stack>
                            
                            <DndProvider backend={HTML5Backend}>
                                <Table autoHeight affixHeader data={selectedArticulos} id="tablePreview" virtualized
                                    sortColumn={sortColumn} locale={{ emptyMessage: "Aún sin artículos ingresados" }}
                                    sortType={sortType} loading={loadingArticulosComplete}
                                    onSortColumn={handleSortColumn}
                                    rowKey="value"
                                    renderRow={(children, rowData) => {
                                        return rowData ? (
                                            <TableRow
                                                key={rowData.value}
                                                rowData={rowData}
                                                id={rowData.value}
                                                onDrag={handleDragRow}
                                            >
                                                {children}
                                            </TableRow>
                                        ) : (
                                            children
                                        );
                                    }}
                                >
                                    <Column width={80} >
                                        <HeaderCell>Cod.</HeaderCell>
                                        <Cell align="center" dataKey="value" />
                                    </Column>
                                    <Column flexGrow={2} >
                                        <HeaderCell>Descripción</HeaderCell>
                                        <Cell dataKey="label" />
                                    </Column>
                                    <Column flexGrow={1} >
                                        <HeaderCell>Proveedor</HeaderCell>
                                        <Cell dataKey="proveedor" />
                                    </Column>
                                    <Column flexGrow={1} >
                                        <HeaderCell>Rubro</HeaderCell>
                                        <Cell dataKey="rubro" />
                                    </Column>
                                    <Column flexGrow={1} >
                                        <HeaderCell>Subrubro</HeaderCell>
                                        <Cell dataKey="subrubro" />
                                    </Column>
                                    {proyectPriceList === 1 && (
                                        <Column width={80} >
                                            <HeaderCell align="right">Lista 1</HeaderCell>
                                            <Cell align="right" dataKey="pventa1">{rowData => `$${" " + Number(rowData.pventa1).toFixed(2)}`}</Cell>
                                        </Column>)
                                    }
                                    {proyectPriceList === 2 && (
                                        <Column width={80} >
                                            <HeaderCell align="right">Lista 2</HeaderCell>
                                            <Cell align="right" dataKey="pventa2">{rowData => `$${" " + Number(rowData.pventa2).toFixed(2)}`}</Cell>
                                        </Column>)
                                    }
                                    {proyectPriceList === 3 && (
                                        <Column width={80} >
                                            <HeaderCell align="right">Lista 3</HeaderCell>
                                            <Cell align="right" dataKey="pventa3">{rowData => `$${" " + Number(rowData.pventa3).toFixed(2)}`}</Cell>
                                        </Column>)
                                    }
                                    {proyectPriceList === 4 && (
                                        <Column width={80} >
                                            <HeaderCell align="right">Lista S</HeaderCell>
                                            <Cell align="right" dataKey="pventaSuc">{rowData => `$${" " + Number(rowData.pventaSuc).toFixed(2)}`}</Cell>
                                        </Column>)
                                    }
                                    </Table>
                            </DndProvider>
                        </Panel>
                    }
                    {
                        currentStep === 5 &&
                        <Panel header={<><h5 className="title">Finalizado</h5><p style={{ opacity: "0.5" }}>
                            El catálogo se generó correctamente! Puede realizar las siguientes acciones
                        </p></>}>
                            <Grid fluid>
                                <Row>
                                    <Col md={16} mdOffset={4}>
                                        <Timeline className="custom-timeline" endless>
                                            {!user.role.includes('user') && ((shareType & 1) == 1) && (
                                                <Timeline.Item dot={<PDFIcon style={{ color: '#333' }} />}>
                                                    <p>Archivo PDF</p>
                                                    <p>
                                                        {!proyectPDF ?
                                                            (
                                                                <Button appearance="link" onClick={() => { setShowPreview(true); }}>Generar archivo PDF.</Button>
                                                            )
                                                            :
                                                            (<>
                                                                <Button appearance="link" onClick={() => { window.open(window.location.origin + '/pdf/' + proyectKey); }}>
                                                                    {window.location.origin}/pdf/{proyectKey}
                                                                </Button>{" "}<IconButton icon={<CopyIcon />} size="sm"
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(window.location.origin + "/pdf/" + proyectKey);
                                                                        toaster.push(messageCopy, { placement: "topCenter", duration: 5000 });
                                                                    }} />
                                                            </>
                                                            )
                                                        }
                                                    </p>
                                                </Timeline.Item>
                                            )}
                                            {((shareType & 2) == 2) && (
                                                <Timeline.Item dot={<ShareIconList style={{ color: '#333' }} />}>
                                                    <p>Compartir en formato lista</p>
                                                    <p><Button appearance="link" onClick={() => { window.open(window.location.origin + '/vl/' + proyectKey); }}>
                                                        {window.location.origin}/vl/{proyectKey}
                                                    </Button>{" "}<IconButton icon={<CopyIcon />} size="sm"
                                                        onClick={() => { navigator.clipboard.writeText(window.location.origin + "/vl/" + proyectKey); toaster.push(messageCopy, { placement: "topCenter", duration: 5000 }); }} /></p>
                                                </Timeline.Item>
                                            )}
                                            {((shareType & 4) == 4) && (
                                                <Timeline.Item dot={<ShareIconList style={{ color: '#333' }} />}>
                                                    <p>Compartir en formato hoja de catálogo</p>
                                                    <p><Button appearance="link" onClick={() => { window.open(window.location.origin + '/vc/' + proyectKey); }}>
                                                        {window.location.origin}/vc/{proyectKey}
                                                    </Button>{" "}<IconButton icon={<CopyIcon />} size="sm"
                                                        onClick={() => { navigator.clipboard.writeText(window.location.origin + "/vc/" + proyectKey); toaster.push(messageCopy, { placement: "topCenter", duration: 5000 }); }} /></p>
                                                </Timeline.Item>
                                            )}
                                        </Timeline>
                                    </Col>
                                </Row>
                            </Grid>
                        </Panel>
                    }
                </Panel>
            </Panel>

            <PDFCreator
                showPreview={showPreview}
                setShowPreview={setShowPreview}
                selectedArticulos={selectedArticulos}
                proyectKey={proyectKey}
                proyectHeaderText={proyectHeaderText}
                proyectName={proyectName}
                proyectPriceList={proyectPriceList}
                setProyectPDF={setProyectPDF}
                proyectGroupType={proyectGroupType}
                proyectUser={{ ...proyectUser, printUser: proyectPrintUser }}
                validityTo={validityTo}
            />

            <Modal backdrop="static" overflow={true} open={showSelectPublish} onClose={() => { setShowSelectPublish(false); setPublishSelected(null); }}>
                <Modal.Header>
                    <Modal.Title>Importar catálogo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <SelectPicker className="select-publish" loading={loadingPublications} size="md" placeholder="Seleccione un catálogo" disabled={globalLoadingData} groupBy="status"
                        data={publications} style={{ display: 'block', marginBottom: 10 }} onChange={(value) => setPublishSelected(publications.find(e => e.value === value))}
                        sort={isGroup => isGroup ? (a, b) => ('' + b).localeCompare(a) : (a, b) => ('' + a).localeCompare(b)}
                        loading={globalLoadingData}                    />
                    {publishSelected != null && (<>
                        <div style={{ paddingTop: 20, paddingBottom: 20, paddingLeft: 10 }}>
                            <p style={{ fontWeight: "normal" }}>Fecha de creación: <span style={{ fontWeight: "bold" }}>{dateToDMYHM(new Date(publishSelected.dateCreated))}</span></p>
                            <p style={{ fontWeight: "normal" }}>Descripción: <span style={{ fontWeight: "bold" }}>{publishSelected.description}</span></p>
                            <p style={{ fontWeight: "normal" }}>Encabezado: <span style={{ fontWeight: "bold" }}>{publishSelected.header}</span></p>
                            <p style={{ fontWeight: "normal" }}>Vigencia: <span style={{ fontWeight: "bold" }}>{dateToDMYHM(new Date(publishSelected.validityFrom)) + ' ~ ' + dateToDMYHM(new Date(publishSelected.validityTo))}</span></p>
                            <p style={{ fontWeight: "normal" }}>Lista de precio <span style={{ fontWeight: "bold" }}>{priceList[publishSelected.priceList]}</span></p>
                            <p style={{ fontWeight: "normal" }}>Cantidad de artículos <span style={{ fontWeight: "bold" }}>{publishSelected.productCount}</span></p>
                        </div>
                    </>)}
                    {publishSelected == null && (<Placeholder.Paragraph rows={5} />)}
                </Modal.Body>
                <Modal.Footer>
                    <Button disabled={publishSelected == null} onClick={handleShowSelectPublishClose} appearance="primary"
                        onClick={() => {
                            setShowSelectPublish(false);
                            if (!dataFetchedFilter.current) {
                                fetchDataFilter();
                                dataFetchedFilter.current = true;
                            }
                            fetchDataFromPublish();
                            setCurrentStep(2);
                        }}>
                        Importar
                    </Button>
                    <Button onClick={() => { setShowSelectPublish(false); setPublishSelected(null); }} appearance="subtle">
                        Cancelar
                    </Button>
                </Modal.Footer>
            </Modal>
            
        </>
    );
}
export default CatalogNew;